import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";

const SelectMultiple = ({
  name,
  change,
  label,
  datas = [],
  dataIndex = "id",
  dataLabel = "name",
  value = [],
  placeholder = "Rechercher...",
  maxSuggestions = 30,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [filteredData, setFilteredData] = useState([]);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Filtrer les données en fonction du terme de recherche
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredData(datas.slice(0, maxSuggestions));
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = datas
      .filter((item) => {
        const itemText = String(item[dataLabel] || "").toLowerCase();
        return itemText.includes(term);
      })
      .slice(0, maxSuggestions);

    setFilteredData(filtered);
  }, [searchTerm, datas, dataLabel, maxSuggestions]);

  // Gérer le clic en dehors du composant
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelect = (item) => {
    const itemValue = item[dataIndex]; // Utiliser dataIndex au lieu de dataLabel

    if (!value.includes(itemValue)) {
      const newValue = [...value, itemValue];
      change({
        target: {
          name,
          value: newValue,
        },
      });
    }
    setSearchTerm("");
    inputRef.current.focus();
  };

  const handleRemove = (itemValue, e) => {
    e.stopPropagation();
    // Trouver l'item correspondant à la valeur d'affichage
    const item = datas.find(
      (item) => String(item[dataLabel]) === String(itemValue)
    );
    // Si on trouve l'item, utiliser sa valeur dataIndex, sinon utiliser la valeur telle quelle
    const valueToRemove = item ? item[dataIndex] : itemValue;
    const newValue = value.filter((v) => String(v) !== String(valueToRemove));
    change({ target: { name, value: newValue } });
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    if (!isOpen) setIsOpen(true);
  };

  const handleInputFocus = () => {
    if (!isOpen) setIsOpen(true);
  };

  const getItemLabel = (item) => {
    return item[dataLabel] || String(item[dataIndex]);
  };

  // Récupérer les éléments sélectionnés complets
  const selectedItems = value
    .map((val) => datas.find((item) => String(item[dataIndex]) === String(val)))
    .filter(Boolean);

  return (
    <div className="form-group" ref={wrapperRef}>
      {label && <label htmlFor={name}>{label}</label>}

      <div className="position-relative">
        <div
          className="form-control d-flex flex-wrap align-items-center"
          style={{ minHeight: "38px", height: "auto", cursor: "text" }}
          onClick={() => inputRef.current.focus()}
        >
          {/* Tags des éléments sélectionnés */}
          {selectedItems.map((item, index) => {
            const itemValue = item[dataLabel];
            return (
              <span
                key={`${itemValue}-${index}`}
                className="badge badge-pill badge-primary mr-1 mb-1 d-flex align-items-center"
                style={{ fontSize: "0.8rem" }}
              >
                {getItemLabel(item)}
                <button
                  type="button"
                  className="close ml-1"
                  aria-label="Supprimer"
                  onClick={(e) => handleRemove(itemValue, e)}
                  style={{ fontSize: "1rem", lineHeight: "1" }}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </span>
            );
          })}

          {/* Champ de recherche */}
          <input
            ref={inputRef}
            type="text"
            className="border-0 flex-grow-1"
            style={{ minWidth: "100px", outline: "none" }}
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            placeholder={value.length === 0 ? placeholder : ""}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-controls={`${name}-dropdown`}
          />
        </div>

        {/* Dropdown des suggestions */}
        {isOpen && filteredData.length > 0 && (
          <div
            className="dropdown-menu show w-100"
            id={`${name}-dropdown`}
            style={{
              maxHeight: "250px",
              overflowY: "auto",
              zIndex: 1060,
            }}
            role="listbox"
          >
            {filteredData.map((item, index) => {
              const itemValue = item[dataLabel];
              const isSelected = value.includes(itemValue);
              return (
                <button
                  key={`${itemValue}-${index}`}
                  type="button"
                  className={`dropdown-item ${isSelected ? "active" : ""}`}
                  onClick={() => handleSelect(item)}
                  role="option"
                  aria-selected={isSelected}
                  disabled={isSelected}
                >
                  {getItemLabel(item)}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

SelectMultiple.propTypes = {
  name: PropTypes.string.isRequired,
  change: PropTypes.func.isRequired,
  label: PropTypes.string,
  datas: PropTypes.arrayOf(PropTypes.object),
  dataIndex: PropTypes.string,
  dataLabel: PropTypes.string,
  value: PropTypes.arrayOf(PropTypes.string),
  placeholder: PropTypes.string,
  maxSuggestions: PropTypes.number,
};

export default SelectMultiple;
